import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  Link2,
  Loader2,
  Calendar,
  MapPin,
  DollarSign,
  Mail,
  User,
  Building2,
  ExternalLink,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import LinkifiedText from '../../components/LinkifiedText';
import { Textarea } from '../../components/ui/textarea';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

/**
 * Admin Booking Requests dashboard
 *
 * Lists every booking request coming in through talent direct-booking links
 * (/book/<slug>). Filter by status, click a row to see full detail + the
 * platform-fee split + the shift/booking ids created when the talent accepts.
 */
const STATUS_CONFIG = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', className: 'bg-emerald-100 text-emerald-700' },
  declined: { label: 'Declined', className: 'bg-red-100 text-red-700' },
  completed: { label: 'Completed', className: 'bg-sky-100 text-sky-700' },
  expired: { label: 'Expired', className: 'bg-gray-100 text-gray-600' },
};

const STATUSES = ['pending', 'accepted', 'declined', 'completed', 'expired'];

const BookingRequestsAdmin = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status === 'all' ? {} : { status };
      const res = await api.get('/admin/booking-requests', { params });
      setRequests(res.data.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load booking requests');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  // Clear the decline reason whenever we switch to a different request
  useEffect(() => {
    setDeclineReason('');
  }, [selected?.id]);

  const accept = async () => {
    if (!selected?.id) return;
    setActioning(true);
    try {
      const res = await api.post(`/admin/booking-requests/${selected.id}/accept`);
      toast.success('Booking accepted on behalf of talent');
      setSelected(res.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to accept booking');
    } finally {
      setActioning(false);
    }
  };

  const decline = async () => {
    if (!selected?.id) return;
    const reason = declineReason.trim();
    if (!reason) {
      toast.error('Please provide a decline reason');
      return;
    }
    setActioning(true);
    try {
      const res = await api.post(`/admin/booking-requests/${selected.id}/decline`, { reason });
      toast.success('Booking declined');
      setSelected(res.data);
      setDeclineReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to decline booking');
    } finally {
      setActioning(false);
    }
  };

  const stats = useMemo(() => {
    const out = { total: requests.length };
    STATUSES.forEach((s) => {
      out[s] = requests.filter((r) => r.status === s).length;
    });
    return out;
  }, [requests]);

  const feeSplit = (req) => {
    if (!req?.hourly_rate) return null;
    const pct = req.platform_fee_pct ?? 15;
    const talentPay = +(req.hourly_rate * (1 - pct / 100)).toFixed(2);
    const platformFee = +(req.hourly_rate * (pct / 100)).toFixed(2);
    return { bill: req.hourly_rate, talentPay, platformFee, pct };
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-booking-requests-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/home')}
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin
          </Button>
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-cyan-600" />
            <h1 className="text-lg font-semibold">Booking Requests</h1>
            <Badge className="bg-pink-100 text-pink-700 border-0 ml-1">New</Badge>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-5 space-y-4">
        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <Card>
            <CardContent className="py-3 text-center">
              <div className="text-2xl font-semibold text-gray-900">{stats.total}</div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Total</div>
            </CardContent>
          </Card>
          {STATUSES.map((s) => (
            <Card key={s}>
              <CardContent className="py-3 text-center">
                <div className={`text-2xl font-semibold ${STATUS_CONFIG[s].className.split(' ')[1]}`}>
                  {stats[s] || 0}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">
                  {STATUS_CONFIG[s].label}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filter row */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 uppercase tracking-wide">Filter</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[200px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_CONFIG[s].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="refresh-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
          </div>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-sm text-gray-500">
              No booking requests yet. Talent share links generate entries here as
              soon as clients submit.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="booking-requests-table">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-3 py-2">Client</th>
                      <th className="text-left px-3 py-2">Talent</th>
                      <th className="text-left px-3 py-2">Event</th>
                      <th className="text-left px-3 py-2">Rate</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => setSelected(r)}
                        className="border-t border-gray-100 hover:bg-cyan-50 cursor-pointer"
                        data-testid={`booking-request-row-${r.id}`}
                      >
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{r.client_name}</div>
                          <div className="text-xs text-gray-500">{r.client_email}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{r.talent_name || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="text-gray-800">{r.event_date ? formatDateMMDDYYYY(r.event_date) : '—'}</div>
                          <div className="text-xs text-gray-500">
                            {[r.event_city, r.event_state].filter(Boolean).join(', ') || 'TBD'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-800">
                          {r.hourly_rate ? `$${r.hourly_rate}/hr` : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={`${STATUS_CONFIG[r.status]?.className || 'bg-gray-100 text-gray-700'} border-0`}>
                            {STATUS_CONFIG[r.status]?.label || r.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-white max-w-2xl" data-testid="request-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Booking Request
              {selected?.status && (
                <Badge className={`${STATUS_CONFIG[selected.status]?.className} border-0 text-[11px]`}>
                  {STATUS_CONFIG[selected.status]?.label}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Direct booking request submitted through a talent's personal share link.
              Review the event details + fee split, then accept or decline on behalf of the talent.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Client</div>
                    <div className="font-medium">{selected.client_name}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Email</div>
                    <div className="font-medium">{selected.client_email}</div>
                  </div>
                </div>
                {selected.company_name && (
                  <div className="flex items-start gap-2">
                    <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-500">Company</div>
                      <div className="font-medium">{selected.company_name}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Talent</div>
                    <div className="font-medium">{selected.talent_name || selected.talent_id}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Event Date</div>
                    <div className="font-medium">
                      {selected.event_date ? formatDateMMDDYYYY(selected.event_date) : '—'}
                      {selected.event_start_time && ` · ${selected.event_start_time}`}
                      {selected.event_end_time && `–${selected.event_end_time}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Location</div>
                    <div className="font-medium">
                      {[selected.event_city, selected.event_state].filter(Boolean).join(', ') || 'TBD'}
                    </div>
                    {selected.venue_address && (
                      <div className="text-xs text-gray-500">{selected.venue_address}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Fee split */}
              {selected.hourly_rate && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3" data-testid="fee-split">
                  <div className="text-xs uppercase tracking-wide text-cyan-700 font-semibold flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Direct-booking fee split
                  </div>
                  {(() => {
                    const split = feeSplit(selected);
                    if (!split) return null;
                    return (
                      <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                        <div>
                          <div className="text-base font-semibold text-gray-900">${split.bill}</div>
                          <div className="text-[10px] text-gray-500">Client bills (/hr)</div>
                        </div>
                        <div>
                          <div className="text-base font-semibold text-emerald-700">${split.talentPay}</div>
                          <div className="text-[10px] text-gray-500">Talent pay (/hr)</div>
                        </div>
                        <div>
                          <div className="text-base font-semibold text-cyan-700">
                            ${split.platformFee} <span className="text-[10px]">({split.pct}%)</span>
                          </div>
                          <div className="text-[10px] text-gray-500">Platform fee (/hr)</div>
                        </div>
                      </div>
                    );
                  })()}
                  {selected.hours_estimated && (
                    <div className="text-[11px] text-gray-500 mt-2 text-center">
                      Estimated {selected.hours_estimated} hrs
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  Event details
                </div>
                <div className="bg-white border border-gray-200 rounded p-2 text-gray-800">
                  <LinkifiedText text={selected.event_details} />
                </div>
              </div>

              {selected.decline_reason && (
                <div>
                  <div className="text-xs text-red-600 uppercase tracking-wide mb-1">
                    Decline reason
                  </div>
                  <div className="text-gray-700">{selected.decline_reason}</div>
                </div>
              )}

              {selected.booking_id && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="linked-booking">
                  <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                    Direct-booking shift created
                  </div>
                  <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-3">
                    <span><span className="font-medium">Shift:</span> {selected.shift_id}</span>
                    <span><span className="font-medium">Booking:</span> {selected.booking_id}</span>
                  </div>
                  {selected.shift_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => navigate(`/admin/shifts/${selected.shift_id}`)}
                      data-testid="goto-shift-btn"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> Open shift
                    </Button>
                  )}
                </div>
              )}

              {/* Admin actions — only on pending requests */}
              {selected.status === 'pending' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3" data-testid="admin-actions">
                  <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">
                    Admin actions
                  </div>
                  <p className="text-xs text-gray-600">
                    Accept on behalf of the talent (fires client email + creates the direct-booking shift) or decline
                    with a reason (visible to both sides).
                  </p>
                  <Textarea
                    rows={2}
                    placeholder="Decline reason (required only for decline)"
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    disabled={actioning}
                    data-testid="admin-decline-reason-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={accept}
                      disabled={actioning}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="admin-accept-btn"
                    >
                      {actioning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Accept
                    </Button>
                    <Button
                      onClick={decline}
                      disabled={actioning}
                      variant="destructive"
                      className="flex-1"
                      data-testid="admin-decline-btn"
                    >
                      {actioning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                      Decline
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingRequestsAdmin;
